import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { type CommonCodeSearchData, searchCommonCodes } from '@/shared/apis/common-codes';

interface UseCommonCodeSearchProps {
  upperCode: string;
  keyword?: string;
  limit?: number;
  // Off until a picker is opened: the first page is only worth fetching for
  // someone who is looking at it.
  enabled?: boolean;
}

const EMPTY: CommonCodeSearchData = { total_count: 0, list: [] };

// Long enough that typing a word is one request, short enough to feel live.
const DEBOUNCE_MS = 250;

/**
 * Keyword search over one cm_code group, for groups too large to ship whole.
 *
 * Debounced here rather than in the component so every caller gets the same
 * behaviour, and `keepPreviousData` holds the last page on screen while the
 * next one loads - without it the list empties on every keystroke and the
 * dropdown flickers.
 */
const useCommonCodeSearch = ({
  upperCode,
  keyword = '',
  limit = 50,
  enabled = true,
}: UseCommonCodeSearchProps) => {
  const [debouncedKeyword, setDebouncedKeyword] = useState(keyword);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedKeyword(keyword), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [keyword]);

  const { data, error, isFetching } = useQuery({
    queryKey: ['commonCodeSearch', upperCode, debouncedKeyword, limit],
    queryFn: () => searchCommonCodes(upperCode, debouncedKeyword, limit),
    enabled: enabled && Boolean(upperCode),
    staleTime: 1000 * 60 * 5,
    placeholderData: keepPreviousData,
  });

  if (error) {
    console.error('Error searching common codes:', error);
  }

  return {
    results: data ?? EMPTY,
    // True while the debounce is still pending too, so the dropdown says
    // "loading" from the first keystroke instead of after it.
    isLoading: isFetching || debouncedKeyword !== keyword,
    error,
  };
};

export default useCommonCodeSearch;
